import {
    binLabel,
    countdownDays,
    deltaCompact,
    deltaLabel,
    localDate,
    localDateTime,
    pct,
    pctInt,
    tsDate,
    utcDateTime,
    utcDayShort,
} from '../chart/format';
import { binColor, directionColor } from '../chart/palette';
import { toneColor } from './layers/decisionTone';
import type { Hover, Point } from './model';
import type { Strings } from './strings';
import type { PiprBundle } from './types';

export interface SummaryBin {
    label: string;
    pctLabel: string;
    color: string;
    share: number;
    isModal: boolean;
}

export interface SummaryStat {
    label: string;
    value: string;
    sub?: string;
    color?: string;
}

export interface Summary {
    headline: string;
    headlineCaption: string;
    anchor: SummaryStat;
    decided?: SummaryStat;
    priced: SummaryStat;
    decision: SummaryStat;
    meeting: SummaryStat;
    binsLabel: string;
    binsNote: string;
    bins: SummaryBin[];
    note: string;
    isLive: boolean;
    meetingIdx: number | null;
}

function modalIndex(p: readonly number[]): number {
    let best = 0;
    for (let j = 1; j < p.length; j++) if (p[j] > p[best]) best = j;
    return best;
}

function bins(p: readonly number[], deltas: readonly number[]): SummaryBin[] {
    const modal = modalIndex(p);
    return p.map((q, j) => ({
        label: binLabel(deltas[j], j, deltas.length),
        pctLabel: pctInt(q),
        color: binColor(deltas[j]),
        share: q / 1000,
        isModal: j === modal,
    }));
}

function decisionValue(delta: number, s: Strings): string {
    if (delta === 0) return s.hold;
    const word = delta > 0 ? s.hike : s.cut;
    return word + ' (' + deltaLabel(delta) + ')';
}

function likely(
    p: readonly number[],
    deltas: readonly number[],
    s: Strings,
    ink: string
): SummaryStat {
    const j = modalIndex(p);
    return {
        label: s.decisionLikely,
        value: decisionValue(deltas[j], s),
        sub: pctInt(p[j]) + ' ' + s.probability,
        color: toneColor(deltas[j], ink),
    };
}

function settled(
    decidedBp: number,
    p: readonly number[],
    deltas: readonly number[],
    s: Strings,
    ink: string
): SummaryStat {
    const j = deltas.indexOf(decidedBp);
    return {
        label: s.decisionMade,
        value: decisionValue(decidedBp, s),
        sub: j >= 0 ? pctInt(p[j]) + ' ' + s.pricedThen : undefined,
        color: toneColor(decidedBp, ink),
    };
}

function decidedStat(
    anchorBp: number,
    pendingBp: number,
    effectiveTs: number | null,
    s: Strings
): SummaryStat {
    return {
        label: s.decided,
        value: pct(anchorBp + pendingBp, 2),
        sub: effectiveTs
            ? s.effectiveFrom.replace('{d}', utcDayShort(tsDate(effectiveTs)))
            : s.notYetEffective,
        color: directionColor(pendingBp),
    };
}

function binsNote(
    p: readonly number[],
    deltas: readonly number[],
    s: Strings
): string {
    const j = modalIndex(p);
    return (
        s.modal +
        ' ' +
        binLabel(deltas[j], j, deltas.length) +
        '  ·  ' +
        pctInt(p[j])
    );
}

export function forwardMeetingIndex(
    data: PiprBundle,
    i: number
): number | null {
    const ts = data.forward.meetings[i]?.decisionTs;
    if (ts === undefined) return null;
    const idx = data.meetings.findIndex(m => m.decisionTs === ts);
    return idx >= 0 ? idx : null;
}

export function buildSummary(
    data: PiprBundle,
    points: readonly Point[],
    hover: Hover,
    s: Strings,
    ink: string
): Summary | null {
    const deltas = data.history.deltas;

    if (hover?.kind === 'hist') {
        const d = points[hover.i];
        if (d) {
            const meeting = data.meetings[d.meetingIdx];
            const when = meeting ? tsDate(meeting.decisionTs) : d.date;
            const obsSec = Math.floor(d.date.getTime() / 1000);
            const change = d.implied - d.anchor - d.pending;
            const decided =
                meeting && meeting.decisionTs <= data.forward.asofTs
                    ? meeting.decidedBp
                    : null;

            return {
                headline: pct(d.implied, 3),
                headlineCaption: s.impliedFor + ' ' + localDate(when),
                anchor: {
                    label: d.pending !== 0 ? s.inForce : s.anchorPlain,
                    value: pct(d.anchor, 2),
                },
                decided:
                    d.pending !== 0
                        ? decidedStat(d.anchor, d.pending, null, s)
                        : undefined,
                priced: {
                    label: s.pricedIn,
                    value: deltaCompact(change),
                    color: directionColor(change),
                },
                decision:
                    decided !== null
                        ? settled(decided, d.p, deltas, s, ink)
                        : likely(d.p, deltas, s, ink),
                meeting: {
                    label: decided !== null ? s.meetingOn : s.pricedMeeting,
                    value: localDate(when),
                    sub:
                        meeting && decided === null
                            ? countdownDays(obsSec, meeting.decisionTs)
                            : undefined,
                },
                binsLabel: s.distribution + ' ' + localDate(when),
                binsNote: binsNote(d.p, deltas, s),
                bins: bins(d.p, deltas),
                note:
                    localDateTime(d.date) +
                    '  ·  ' +
                    s.close +
                    ' ' +
                    utcDateTime(d.date) +
                    ' UTC',
                isLive: false,
                meetingIdx: meeting ? d.meetingIdx : null,
            };
        }
    }

    if (hover?.kind === 'fwd') {
        const m = data.forward.meetings[hover.i];
        if (m) {
            const when = tsDate(m.decisionTs);
            const change = m.rateBp - m.baseBp;

            return {
                headline: pct(m.rateBp, 2),
                headlineCaption: s.impliedFor + ' ' + localDate(when),
                anchor: { label: s.basisBefore, value: pct(m.baseBp, 2) },
                priced: {
                    label: s.pricedIn,
                    value: deltaCompact(change),
                    color: directionColor(change),
                },
                decision: likely(m.p, deltas, s, ink),
                meeting: {
                    label: s.pricedMeeting,
                    value: localDate(when),
                    sub: countdownDays(data.forward.asofTs, m.decisionTs),
                },
                binsLabel: s.distribution + ' ' + localDate(when),
                binsNote: binsNote(m.p, deltas, s),
                bins: bins(m.p, deltas),
                note: s.decision + ' ' + utcDateTime(when) + ' UTC',
                isLive: false,
                meetingIdx: forwardMeetingIndex(data, hover.i),
            };
        }
    }

    const front = data.forward.meetings[0];
    if (!front) return null;

    const fwd = data.forward;
    const when = tsDate(front.decisionTs);
    const change = front.rateBp - front.baseBp;
    const pendingOpen = fwd.pendingBp !== 0;

    return {
        headline: pct(front.rateBp, 2),
        headlineCaption: s.impliedFor + ' ' + localDate(when),
        anchor: {
            label: pendingOpen ? s.inForce : s.anchorToday,
            value: pct(fwd.anchorBp, 2),
        },
        decided: pendingOpen
            ? decidedStat(
                  fwd.anchorBp,
                  fwd.pendingBp,
                  fwd.pendingEffectiveTs,
                  s
              )
            : undefined,
        priced: {
            label: s.pricedIn,
            value: deltaCompact(change),
            color: directionColor(change),
        },
        decision: likely(front.p, deltas, s, ink),
        meeting: {
            label: s.nextMeeting,
            value: localDate(when),
            sub: countdownDays(fwd.asofTs, front.decisionTs),
        },
        binsLabel: s.distribution + ' ' + localDate(when),
        binsNote: binsNote(front.p, deltas, s),
        bins: bins(front.p, deltas),
        note:
            pendingOpen && fwd.pendingAnnouncedTs
                ? s.announcedOn.replace(
                      '{d}',
                      utcDayShort(tsDate(fwd.pendingAnnouncedTs))
                  )
                : '',
        isLive: true,
        meetingIdx: forwardMeetingIndex(data, 0),
    };
}
